import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Change, Client } from '../../models';
import { useAppSelector, useAppDispatch } from '../../redux';
import { loadActiveTrack, createTrack, stopActiveTrack, updateChange } from '../../redux';
import { Icon, Tooltip, TrackClientPromptModal } from '..';
import { hasExplicitClient, resolveTrackClient } from '../../utils/resolveTrackClient';

interface ChangeTrackProps {
    change: Change;
    className?: string;
}

function ChangeTrack(props: ChangeTrackProps) {
    const { change, className } = props;

    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();

    const { user }                                  = useAppSelector(state => state.auth);
    const { projects }                              = useAppSelector(state => state.projects);
    const { active, loadingActive, stoppingActive, creating } = useAppSelector(state => state.tracks);
    const [promptOpen, setPromptOpen]               = useState(false);

    const buildTitle = () => {
        let title = `${change.slugName()} - ${change.localizedTitle(i18n.language)}`;
        if (change.isInReview(/*onlyByTeam*/true) && user?._id !== change.assignee?._id)
            title = '[Review] ' + title;
        return title;
    };

    const assignClientToChangeIfNeeded = (client: Client | AnyObj | string) => {
        if (!change?._id || hasExplicitClient(change))
            return;
        dispatch(updateChange({ _id: change._id, client }, /*patch*/true, /*optimistic*/true));
    };

    const startWithClient = (client: Client | AnyObj | string) => {
        assignClientToChangeIfNeeded(client);
        dispatch(createTrack({
            change,
            title: buildTitle(),
            project: change.project,
            client,
        }, {}, (err: Error | null) => {
            if (!err) {
                setPromptOpen(false);
                dispatch(loadActiveTrack());
            }
        }));
    };

    const start = (e: AnyObj) => {
        e.stopPropagation();

        const client = resolveTrackClient(change, projects);
        if (!client) {
            setPromptOpen(true);
            return;
        }

        startWithClient(client);
    };

    const stop = (e: AnyObj) => {
        e.stopPropagation();
        dispatch(stopActiveTrack());
    };

    if (!change)
        return null;

    const isLoading = (
        loadingActive === 'pending' ||
        stoppingActive === 'pending' ||
        creating === 'pending'
    );

    if (!user?.can('track:create'))
        return null;

    const isActive = active?.change?._id === change._id;

    return (
        <div className={`group/track ${className || ''} cursor-pointer inline-flex items-center justify-center`}>
            {!isActive && (
                <Tooltip content={t('changes.help.track_start') || ''} light>
                    <div onClick={start} className="inline-flex items-center justify-center">
                        <Icon type={isLoading ? 'loading' : 'start'} color="blue-500" size={6} />
                    </div>
                </Tooltip>
            )}
            {isActive && (
                <Tooltip content={t('changes.help.track_stop') || ''} light>
                    <div onClick={stop} className="grid h-6 w-6 place-items-center">
                        <Icon
                            type="running"
                            className="col-start-1 row-start-1 group-hover/track:invisible"
                            color="blue-500"
                            size={6}
                        />
                        <Icon
                            type={isLoading ? 'loading' : 'stop'}
                            className="col-start-1 row-start-1 invisible group-hover/track:visible"
                            color="red-500"
                            size={6}
                        />
                    </div>
                </Tooltip>
            )}
            <TrackClientPromptModal
                show={promptOpen}
                loading={creating === 'pending'}
                onClose={() => setPromptOpen(false)}
                onConfirm={startWithClient}
            />
        </div>
    );
}

export default ChangeTrack;
